import React, { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useConfirm } from 'material-ui-confirm';

import Box from '@mui/material/Box';

import type { PaymentGridColumn } from '@/domain/payments/types';
import { useUpdateBeneficiaryMutation, useDeleteBeneficiaryMutation } from '@/domain/payments/queries';
import { yieldSuccessNotification } from '@/lib/toasts';
import BeneficiaryModal from '@/pages/dokkaPay/pages/payment/components/PaymentDetails/components/PaymentInfo/components/BeneficiaryModal';

import BeneficiaryCard, { BeneficiaryItem } from '@/pages/dokkaPay/pages/configurations/company/pages/beneficiaries/components/BeneficiaryDetailCellRenderer/BeneficiaryCard';
import DeleteDefaultBeneficiaryDialog, { beneficiaryLabel } from '@/pages/dokkaPay/pages/configurations/company/pages/beneficiaries/components/BeneficiaryDetailCellRenderer/DeleteDefaultBeneficiaryDialog';

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: Record<string, any>;
  beneficiaryHeaders: PaymentGridColumn[];
  onSoftRefresh: () => void;
}

const BeneficiaryDetailCellRenderer: React.FC<Props> = ({ data, beneficiaryHeaders, onSoftRefresh }) => {
  const { formatMessage } = useIntl();
  const confirm = useConfirm();
  const updateBeneficiary = useUpdateBeneficiaryMutation();
  const deleteBeneficiary = useDeleteBeneficiaryMutation();

  const [beneficiaries, setBeneficiaries] = useState<BeneficiaryItem[]>(
    (data.beneficiaries as BeneficiaryItem[]) ?? [],
  );
  const [editingBeneficiary, setEditingBeneficiary] = useState<BeneficiaryItem | null>(null);
  const [pendingDefaultDelete, setPendingDefaultDelete] = useState<BeneficiaryItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const applyDeletion = useCallback(
    async (id: string, newDefaultId?: string): Promise<boolean> => {
      setIsDeleting(true);
      try {
        if (newDefaultId) {
          try {
            await updateBeneficiary.mutateAsync({ path: { id: newDefaultId }, body: { isDefault: true } });
          } catch {
            return false;
          }
        }
        try {
          await deleteBeneficiary.mutateAsync({ path: { id } });
        } catch {
          if (newDefaultId) {
            setBeneficiaries((prev) => prev.map((b) => ({ ...b, isDefault: b.id === newDefaultId })));
          }
          return false;
        }
        setBeneficiaries((prev) =>
          prev
            .filter((b) => b.id !== id)
            .map((b) => (newDefaultId && b.id === newDefaultId ? { ...b, isDefault: true } : b)),
        );
        onSoftRefresh();
        yieldSuccessNotification(
          formatMessage({
            id: 'payment.toasts.beneficiary.delete.success',
            defaultMessage: 'Beneficiary deleted',
          }),
        );
        return true;
      } finally {
        setIsDeleting(false);
      }
    },
    [updateBeneficiary, deleteBeneficiary, formatMessage, onSoftRefresh],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      if (isDeleting) return;
      const target = beneficiaries.find((b) => b.id === id);
      const remaining = beneficiaries.filter((b) => b.id !== id);

      if (target?.isDefault && remaining.length > 1) {
        setPendingDefaultDelete(target);
        return;
      }

      try {
        await confirm(
          target?.isDefault
            ? {
                title: formatMessage({
                  id: 'beneficiary.delete.default.confirm.title',
                  defaultMessage: 'Delete default beneficiary?',
                }),
                description: (
                  <FormattedMessage
                    id="beneficiary.delete.default.confirm.description"
                    defaultMessage="<strong>{name}</strong> is your current default beneficiary that will be deleted. This action can't be undone."
                    values={{
                      name: beneficiaryLabel(target),
                      strong: (chunks) => <strong>{chunks}</strong>,
                    }}
                  />
                ),
                confirmationText: formatMessage({ id: 'button.delete', defaultMessage: 'Delete' }),
                cancellationText: formatMessage({ id: 'button.cancel', defaultMessage: 'Cancel' }),
                confirmationButtonProps: { color: 'error' },
                allowClose: false,
              }
            : {
                title: formatMessage({ id: 'beneficiary.delete.confirm.title', defaultMessage: 'Delete beneficiary?' }),
                description: formatMessage({
                  id: 'beneficiary.delete.confirm.description',
                  defaultMessage: 'This action cannot be undone!',
                }),
                confirmationText: formatMessage({ id: 'button.delete', defaultMessage: 'Delete' }),
                cancellationText: formatMessage({ id: 'button.cancel', defaultMessage: 'Cancel' }),
                confirmationButtonProps: { color: 'error' },
                allowClose: false,
              },
        );
      } catch {
        return;
      }
      applyDeletion(id);
    },
    [beneficiaries, confirm, formatMessage, applyDeletion, isDeleting],
  );

  const handleDefaultDeleteConfirm = useCallback(
    async (newDefaultId: string) => {
      if (!pendingDefaultDelete) return;
      const deleted = await applyDeletion(pendingDefaultDelete.id, newDefaultId);
      if (deleted) setPendingDefaultDelete(null);
    },
    [pendingDefaultDelete, applyDeletion],
  );

  const handleSetDefault = useCallback(
    (id: string) => {
      updateBeneficiary.mutate(
        { path: { id }, body: { isDefault: true } },
        {
          onSuccess: () => {
            setBeneficiaries((prev) => prev.map((b) => ({ ...b, isDefault: b.id === id })));
            onSoftRefresh();
          },
        },
      );
    },
    [updateBeneficiary, onSoftRefresh],
  );

  const handleEditSubmit = useCallback(
    (submitData: Record<string, unknown>): Promise<unknown> => {
      if (!editingBeneficiary) return Promise.resolve();
      const { details, isDefault, source } = submitData as {
        details?: Record<string, string | undefined>;
        isDefault?: boolean;
        source?: 'erp' | 'user';
      };
      return new Promise<void>((resolve, reject) => {
        updateBeneficiary.mutate(
          { path: { id: editingBeneficiary.id }, body: { details, isDefault, source } },
          {
            onSuccess: () => {
              setBeneficiaries((prev) =>
                prev.map((b) => {
                  if (b.id === editingBeneficiary.id) return { ...b, ...(details ?? {}), isDefault: isDefault ?? b.isDefault };
                  return isDefault ? { ...b, isDefault: false } : b;
                }),
              );
              onSoftRefresh();
              resolve();
            },
            // Reject so the modal stays open + preserves input on a failed update.
            onError: (err) => reject(err),
          },
        );
      });
    },
    [updateBeneficiary, editingBeneficiary, onSoftRefresh],
  );

  const handleWheelCapture = useCallback((e: React.WheelEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <Box onWheelCapture={handleWheelCapture} sx={{ p: 2, bgcolor: 'background.default' }}>
      {beneficiaries.map((ben) => (
        <BeneficiaryCard
          key={ben.id}
          beneficiary={ben}
          headers={beneficiaryHeaders}
          onEdit={() => setEditingBeneficiary(ben)}
          onDelete={() => handleDelete(ben.id)}
          onSetDefault={() => handleSetDefault(ben.id)}
        />
      ))}

      {pendingDefaultDelete && (
        <DeleteDefaultBeneficiaryDialog
          beneficiary={pendingDefaultDelete}
          candidates={beneficiaries.filter((b) => b.id !== pendingDefaultDelete.id)}
          isSubmitting={isDeleting}
          onClose={() => setPendingDefaultDelete(null)}
          onConfirm={handleDefaultDeleteConfirm}
        />
      )}

      {editingBeneficiary && (
        <BeneficiaryModal
          onClose={() => setEditingBeneficiary(null)}
          onSubmit={handleEditSubmit}
          vendorId={data.vendorId}
          vendorName={data.vendorDisplayName}
          existingAccountsCount={beneficiaries.length}
          beneficiaryId={editingBeneficiary.id}
          isEditable
          isDefault={editingBeneficiary.isDefault}
        />
      )}
    </Box>
  );
};

export default BeneficiaryDetailCellRenderer;
